import React, { forwardRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';

const PrintTemplate = forwardRef(({ 
  limaDeliveries = [], 
  provinceDeliveries = [],
  dropshipLima = [],
  dropshipProvince = [],
  printerType 
}, ref) => {
  // Combine all deliveries with type markers
  const allDeliveries = [
    ...limaDeliveries.map(d => ({ ...d, type: 'lima' })),
    ...provinceDeliveries.map(d => ({ ...d, type: 'province' })),
    ...dropshipLima.map(d => ({ ...d, type: 'dropship-lima' })),
    ...dropshipProvince.map(d => ({ ...d, type: 'dropship-province' }))
  ];

  return (
    <div ref={ref}>
      {printerType === 'a4' ? (
        <A4PrintLayout deliveries={allDeliveries} />
      ) : (
        <ThermalPrintLayout deliveries={allDeliveries} />
      )}
    </div>
  );
});

PrintTemplate.displayName = 'PrintTemplate';

// A4 Print Layout
function A4PrintLayout({ deliveries }) {
  const pages = [];
  for (let i = 0; i < deliveries.length; i += 8) {
    const pageDeliveries = deliveries.slice(i, i + 8);
    pages.push(
      <div
        key={`page-${i}`}
        style={{
          width: '210mm',
          height: '297mm',
          pageBreakAfter: i + 8 < deliveries.length ? 'always' : 'auto',
          fontFamily: 'Montserrat, sans-serif',
          padding: '8mm',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gridTemplateRows: 'repeat(4, 1fr)',
          gap: '6mm',
          width: '100%',
          height: '100%'
        }}>
          {pageDeliveries.map((dp) => (
            <LabelA4 key={`${dp.type}-${dp.id}`} dp={dp} />
          ))}
        </div>
      </div>
    );
  }
  return <>{pages}</>;
}

// Thermal Print Layout
function ThermalPrintLayout({ deliveries }) {
  return (
    <div style={{ fontFamily: 'Montserrat, sans-serif' }}>
      {deliveries.map((dp, index) => (
        <LabelThermal 
          key={`${dp.type}-${dp.id}`} 
          dp={dp} 
          isLast={index === deliveries.length - 1}
        />
      ))}
    </div>
  );
}

// A4 Label Component
function LabelA4({ dp }) {
  const isProvince = dp.type === 'province' || dp.type === 'dropship-province';
  const borderColor = isProvince ? '#E6B43C' : '#B1336E';
  const labelColor = isProvince ? '#E6B43C' : '#B1336E';

  return (
    <div
      style={{
        border: `2px solid ${borderColor}`,
        padding: '4mm',
        boxSizing: 'border-box',
        position: 'relative',
        backgroundColor: 'white',
        borderRadius: '3mm',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2mm' }}>
        <p style={{ 
          color: labelColor, 
          fontSize: '18px', 
          fontWeight: 'bold',
          margin: '0'
        }}>
          ID: {dp.id}
        </p>
        {isProvince && (
          <p style={{
            color: labelColor,
            fontSize: '10px',
            fontWeight: 'bold',
            margin: '0'
          }}>
            PROVINCIA
          </p>
        )}
      </div>
      
      <p style={{ 
        color: '#1e293b', 
        fontSize: '13px',
        fontWeight: '600',
        margin: '0 0 1.5mm 0',
        lineHeight: '1.2'
      }}>
        {dp.recipient_name}
      </p>
      
      {isProvince && dp.recipient_dni && (
        <p style={{ 
          color: '#475569', 
          fontSize: '9px',
          margin: '0 0 1.5mm 0'
        }}>
          DNI: {dp.recipient_dni}
        </p>
      )}
      
      {dp.product && (
        <p style={{ 
          color: '#475569', 
          fontSize: '10px',
          margin: '0 0 1.5mm 0',
          lineHeight: '1.2'
        }}>
          {dp.product}
        </p>
      )}
      
      {dp.user_name && (
        <p style={{ 
          color: labelColor, 
          fontSize: '9px',
          fontWeight: '600',
          margin: '0 0 1.5mm 0',
          lineHeight: '1.2'
        }}>
          Vendedor: {dp.user_name}
        </p>
      )}
      
      {isProvince ? (
        <>
          <p style={{ 
            color: '#475569', 
            fontSize: '10px',
            fontWeight: '600',
            margin: '0 0 1mm 0'
          }}>
            {dp.province} - {dp.district}
          </p>
          <p style={{ 
            color: '#475569', 
            fontSize: '8px',
            margin: '0 0 1mm 0',
            lineHeight: '1.2'
          }}>
            {dp.address}
          </p>
          <p style={{ 
            color: labelColor, 
            fontSize: '8px',
            fontWeight: '600',
            margin: '0',
            lineHeight: '1.2'
          }}>
            Agencia: {dp.agency_address}
          </p>
        </>
      ) : (
        <>
          {dp.district && (
            <p style={{ 
              color: '#475569', 
              fontSize: '10px',
              fontWeight: '500',
              margin: '0 0 1.5mm 0',
              lineHeight: '1.2'
            }}>
              {dp.district}
            </p>
          )}
          <p style={{ 
            color: '#475569', 
            fontSize: '9px',
            margin: '0',
            paddingRight: isProvince ? '0' : '28mm',
            lineHeight: '1.3'
          }}>
            {dp.address}
          </p>
          
          {/* QR Code only for Lima deliveries */}
          {!isProvince && (
            <div style={{
              position: 'absolute',
              bottom: '4mm',
              right: '4mm',
              padding: '1.5mm',
              backgroundColor: 'white',
              borderRadius: '2mm'
            }}>
              <QRCodeCanvas
                value={dp.tracking_code}
                size={75}
                level="H"
                includeMargin={false}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Thermal Label Component
function LabelThermal({ dp, isLast }) {
  const isProvince = dp.type === 'province' || dp.type === 'dropship-province';
  const borderColor = isProvince ? '#E6B43C' : '#B1336E';
  const labelColor = isProvince ? '#E6B43C' : '#B1336E';

  return (
    <div
      style={{
        width: '80mm',
        minHeight: isProvince ? '70mm' : '60mm',
        border: `2px solid ${borderColor}`,
        padding: '4mm',
        boxSizing: 'border-box',
        backgroundColor: 'white',
        marginBottom: '5mm',
        pageBreakAfter: isLast ? 'auto' : 'always',
        pageBreakInside: 'avoid',
        borderRadius: '3mm'
      }}
    >
      {!isProvince && (
        <div style={{ textAlign: 'center', marginBottom: '3mm' }}>
          <QRCodeCanvas
            value={dp.tracking_code}
            size={80}
            level="H"
            includeMargin={false}
          />
        </div>
      )}
      
      <div style={{
        textAlign: 'center',
        marginBottom: '3mm',
        borderBottom: isProvince ? `1px solid ${borderColor}` : 'none',
        paddingBottom: isProvince ? '2mm' : '0'
      }}>
        {isProvince && (
          <p style={{
            color: labelColor,
            fontSize: '14px',
            fontWeight: 'bold',
            margin: '0 0 1mm 0'
          }}>
            ENVÍO A PROVINCIA
          </p>
        )}
        <p style={{ 
          color: labelColor, 
          fontSize: '18px', 
          fontWeight: 'bold',
          margin: '0'
        }}>
          ID: {dp.id}
        </p>
      </div>
      
      <p style={{ 
        color: '#1e293b', 
        fontSize: '13px',
        fontWeight: '600',
        margin: '0 0 2mm 0'
      }}>
        {dp.recipient_name}
      </p>
      
      {isProvince && dp.recipient_dni && (
        <p style={{ 
          color: '#475569', 
          fontSize: '10px',
          margin: '0 0 2mm 0'
        }}>
          DNI: {dp.recipient_dni}
        </p>
      )}
      
      {dp.product && (
        <p style={{ 
          color: '#475569', 
          fontSize: '10px',
          margin: '0 0 1mm 0'
        }}>
          {dp.product}
        </p>
      )}
      
      {dp.user_name && (
        <p style={{ 
          color: labelColor, 
          fontSize: '9px',
          fontWeight: '600',
          margin: '0 0 1mm 0'
        }}>
          Vendedor: {dp.user_name}
        </p>
      )}
      
      {isProvince ? (
        <>
          <p style={{ 
            color: '#1e293b', 
            fontSize: '11px',
            fontWeight: '600',
            margin: '0 0 1mm 0'
          }}>
            {dp.province} - {dp.district}
          </p>
          <p style={{ 
            color: '#475569', 
            fontSize: '9px',
            margin: '0 0 2mm 0'
          }}>
            {dp.address}
          </p>
          <div style={{
            backgroundColor: '#FFF9E6',
            padding: '2mm',
            borderRadius: '2mm',
            border: `1px solid ${borderColor}`
          }}>
            <p style={{ 
              color: labelColor, 
              fontSize: '8px',
              fontWeight: '600',
              margin: '0',
              lineHeight: '1.3'
            }}>
              Agencia: {dp.agency_address}
            </p>
          </div>
        </>
      ) : (
        <>
          {dp.district && (
            <p style={{ 
              color: '#475569', 
              fontSize: '10px',
              fontWeight: '500',
              margin: '0 0 1mm 0'
            }}>
              {dp.district}
            </p>
          )}
          <p style={{ 
            color: '#475569', 
            fontSize: '9px',
            margin: '0'
          }}>
            {dp.address}
          </p>
        </>
      )}
    </div>
  );
}

export default PrintTemplate;